import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Delete } from "lucide-react";

interface PasscodeFormProps {
  mode: "setup" | "enter";
  onSubmit: (passcode: string) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

const PIN_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["", "0", "back"],
] as const;

export function PasscodeForm({ mode, onSubmit, loading, error }: PasscodeFormProps) {
  const isSetup = mode === "setup";
  const [digits, setDigits] = useState("");
  const submittedRef = useRef("");
  const maxLen = 8;

  // Auto-submit in enter mode once 4+ digits
  useEffect(() => {
    if (
      !isSetup &&
      digits.length >= 4 &&
      !loading &&
      digits !== submittedRef.current
    ) {
      submittedRef.current = digits;
      onSubmit(digits);
    }
  }, [digits, isSetup, loading, onSubmit]);

  // Reset digits when a new error arrives
  useEffect(() => {
    if (error) {
      setDigits("");
      submittedRef.current = "";
    }
  }, [error]);

  const pressKey = useCallback(
    (key: string) => {
      if (loading) return;
      if (key === "back") {
        setDigits((d) => d.slice(0, -1));
      } else if (key >= "0" && key <= "9") {
        setDigits((d) => (d.length < maxLen ? d + key : d));
      }
    },
    [loading],
  );

  // Support physical keyboard input as well
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Backspace") {
        pressKey("back");
      } else if (e.key >= "0" && e.key <= "9") {
        pressKey(e.key);
      } else if (e.key === "Enter" && isSetup && digits.length >= 4) {
        submittedRef.current = digits;
        onSubmit(digits);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pressKey, isSetup, digits, onSubmit]);

  return (
    <div className="min-h-screen flex items-start pt-8 md:items-center md:pt-0 justify-center bg-app-bg p-2 md:p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-app-primary/10 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-app-primary" />
          </div>
          <CardTitle className="text-heading">
            {isSetup ? "Set Your Passcode" : "Enter Passcode"}
          </CardTitle>
          <CardDescription className="text-info">
            {isSetup
              ? "Choose a passcode you'll remember."
              : "Enter your passcode to continue."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dot display */}
          <div className="flex items-center justify-center gap-2.5 h-12">
            {digits.length === 0 ? (
              <span className="text-sm text-app-text-muted">
                {isSetup ? "Enter at least 4 digits" : ""}
              </span>
            ) : (
              Array.from({ length: digits.length }).map((_, i) => (
                <div
                  key={i}
                  className="w-3.5 h-3.5 rounded-full bg-app-primary animate-in fade-in-0 zoom-in-75 duration-150"
                />
              ))
            )}
          </div>

          {error && (
            <p className="text-info text-app-danger text-center">{error}</p>
          )}

          {isSetup && (
            <div className="rounded-lg bg-app-warning/10 border border-app-warning/30 p-2.5">
              <p className="text-info text-app-warning font-medium text-center">
                ⚠️ Remember this passcode!
              </p>
            </div>
          )}

          {/* PIN pad */}
          <div className="grid grid-cols-3 gap-2.5 max-w-70 mx-auto">
            {PIN_KEYS.flat().map((key, i) => {
              if (key === "") return <div key={i} />;
              if (key === "back") {
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pressKey("back")}
                    disabled={loading || digits.length === 0}
                    className="h-14 rounded-xl flex items-center justify-center text-app-text-muted hover:bg-app-surface-raised active:bg-app-border transition-colors disabled:opacity-30"
                  >
                    <Delete className="w-6 h-6" />
                  </button>
                );
              }
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pressKey(key)}
                  disabled={loading}
                  className="h-14 rounded-xl text-xl font-semibold text-app-text hover:bg-app-surface-raised active:bg-app-border transition-colors"
                >
                  {key}
                </button>
              );
            })}
          </div>

          {/* Submit button — only shown in setup mode */}
          {isSetup && (
            <Button
              type="button"
              className="w-full"
              disabled={loading || digits.length < 4}
              onClick={() => {
                submittedRef.current = digits;
                onSubmit(digits);
              }}
            >
              {loading ? "..." : "Set Passcode"}
            </Button>
          )}

          {loading && !isSetup && (
            <p className="text-center text-sm text-app-text-muted animate-pulse">
              Verifying...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
